import { computed, effect, inject, untracked } from '@angular/core';
import { Router, type Params } from '@angular/router';
import { type PopulationType } from '@bizzdesign/sdk-population';
import { type ObjectType } from '@bizzdesign/sdk-knowledgegraph/bdcore';
import {
  AttributeCategoryDefinitionsService,
  ContentDetailsGlobalStore,
  type ContentItemDetails,
} from '@bizzdesign/data-access-content-details';
import {
  attributeDataPayloadToParameterUpdate,
  type AttributeValueWithParameterKey,
  type ContentSelection,
  defaultAdvancedFilter,
  FilterGlobalStore,
  type FilterItem,
  getFilteredResultsCount,
  getFiltersForObjectsCount,
  getScopeFilterOptions,
  getSortedScopeFilters,
  getUpdatedAdvancedFilter,
  getUpdatedSuggestionFilters,
  nlsQueryOverviewToInteractiveOverview,
  type NlsOverviewParametersMap,
  type NlsQueryInteractiveOverview,
  objectDtoToContentItem,
  PopulationsGlobalStore,
  type ResultSuggestionItem,
  type SearchResultItem,
  searchResultItemToContentItem,
  searchResultToSearchResultItem,
  SearchResultsGlobalStore,
  SearchService,
  SearchSessionService,
  ObjectSearchResultsService,
  ViewSearchResultsService,
  WorkspaceSearchResultsService,
  type SearchSuggestionGroup,
  type SimpleValueWithParameterKey,
  type SmartSuggestionItem,
} from '@bizzdesign/data-access-search';
import {
  mapViewSearchResultsToViewItems,
  viewItemToContentItem,
  type ViewItem,
  type WorkspaceBase,
  type WorkspaceItem,
  workspaceItemToContentItem,
  WorkspacesService,
} from '@bizzdesign/data-access-workspaces';
import { baseRoutes } from '@bizzdesign/shared/utils';
import { FeatureFlagsStore } from '@bizzdesign/shared/feature-flags';
import {
  OBJECT_SELECTION_LIMIT,
  type ElementQuery,
  type AdvancedFilter,
  type AttributeCategoryDefinitionDto,
  type AuthenticatedUser,
  type ObjectDto,
  type SystemAttributesUser,
  type AttributeFilterValue,
  type RelationFilterValue,
  type ElementCollection,
} from '@bizzdesign/shared/models';
import {
  type SearchParameters as SdkSearchParameters,
  type SearchScope,
  type SearchSortInput,
  type SmartCypherSearchResult,
} from '@bizzdesign/sdk-search';
import {
  getState,
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { cloneDeep } from 'lodash-es';
import {
  BehaviorSubject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  firstValueFrom,
  from,
  map,
  type Observable,
  of,
  pipe,
  switchMap,
  tap,
  take,
  finalize,
  takeUntil,
  Subject,
} from 'rxjs';
import {
  getCombinedSuggestions,
  getSearchSuggestions,
  getSmartSearchSuggestionGroup,
  type ResultSuggestionsResponse,
} from '../../utils/search-suggestions.utils';
import { createObjectResultsColumnCatalogue } from '../../utils/object-results-columns.utils';
import {
  cypherSearchToUrlQueryParameters,
  searchParametersToUrlQueryParameters,
  urlParamToCypherSearchId,
  updateAttributeFiltersObjectsNames,
  updateRelationFiltersObjectsNames,
  updateSystemAttributeFilterUserDetails,
  urlParamToRelationFiltersAndObjectIds,
  urlParamToAttributeFiltersAndObjectIds,
  urlParamToElementTypeFilters,
  urlParamToScopeFilters,
  urlParamToSearchPageIndex,
  urlParamToSearchTerm,
  urlParamToSystemAttributesFilter,
  urlParamToObjectId,
  type AttributeFilterValueAndObjectId,
  type RelationFilterValueAndObjectId,
  areScopeFiltersEqual,
  areTypeFiltersEqual,
  areAttributeFiltersEqual,
  areRelationFiltersEqual,
  areSystemFiltersEqual,
  areSearchTermsEqual,
  areSearchPageIndexesEqual,
} from '../../utils/search-url.utils';
import {
  getSelectedContent,
  handleCountSearchResults,
  isObjectFilterItemSelected,
  isObjectFilterAddedOrUpdated,
} from './search-container.component.store.utils';
import { DrawerGlobalStore } from '@bizzdesign/ui-components';
import {
  AiAssistantV2Store,
  type SearchApi,
} from '@bizzdesign/ai-assistant/data-access';
import { ContentOpenService } from '@bizzdesign/feature-content-open';
import {
  type AddToWorkspaceData,
  type CypherSearchData,
  type FilterSearchData,
  type SearchData,
  type SemanticSearchData,
  buildInsightSignal,
  type ElementSummaryItem,
  SEARCH_INITIAL_SUGGESTIONS,
} from '@bizzdesign/ai-assistant/workflows';
import { UsersService } from '@bizzdesign/data-access-users';
import { isNotBlank, isNotUndefinedOrNull } from '@bizzdesign/bizzdesign-sdk';
import {
  isUUID,
  assertIsUUIDs,
  generateUUID,
  type UUID,
} from '@bizzdesign/bizzdesign-sdk/uuid';
import { AccessControlService, SessionService } from '@bizzdesign/auth';
import {
  KnowledgeGraphClientBDCore,
  ProductAnalyticsService,
} from '@bizzdesign/shared/services';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MessageService } from 'primeng/api';

function serializeFilterLabels(filterItems: FilterItem[]): string[] {
  return filterItems
    .map((item) =>
      item.label
        .map((part) => part.text ?? '')
        .filter((text) => text.length > 0)
        .join(' '),
    )
    .filter((label) => label.length > 0);
}

type SearchParameters = {
  searchTerm: string;
  advancedFilter: AdvancedFilter;
  sdkSearchFilter: SdkSearchParameters | undefined;
  sorting?: SearchSortInput | null;
  limit?: number;
};

type ObjectSearchLoadRequest = {
  searchParams: SearchParameters;
  loadMode: 'prepare' | 'refresh';
};

type SmartSearchParameters = {
  searchTerm: string;
  smartFiltersForceRetry: boolean;
};

type SuggestionParameters = {
  typeAheadSearchTerm: string;
  appliedFilters: FilterItem[];
  resultSuggestions: ResultSuggestionsResponse | null;
  aiSearchToggleOn: boolean;
};

type SmartSuggestionParameters = {
  typeAheadSearchTerm: string;
  aiSearchToggleOn: boolean;
  attributeCategoryDefinitions: AttributeCategoryDefinitionDto[];
};

type AttributeFiltersUrlContext = {
  attributeFiltersUrlParam: string | undefined;
  attributeCategoryDefinitions: AttributeCategoryDefinitionDto[];
};

function stringifyAttributeValue(value: unknown): string | number {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    return value;
  }
  return JSON.stringify(value);
}

function toElementSummaryItems(
  collection: ElementCollection,
): ElementSummaryItem[] {
  return collection.objects.map((obj) => ({
    name: obj.name,
    type: obj.type,
    attributes: (obj.attributeCategories ?? []).flatMap((cat) =>
      mapAttributes(cat.name, cat.attributes),
    ),
  }));
}

function mapAttributes(
  categoryName: string,
  attributes: ReadonlyArray<{ name: string; value?: unknown }>,
): NonNullable<ElementSummaryItem['attributes']> {
  return attributes
    .filter((attr) => attr.value !== null && attr.value !== undefined)
    .map((attr) => ({
      categoryName,
      attributeName: attr.name,
      value: stringifyAttributeValue(attr.value),
    }));
}

export type SearchState = {
  contentOpenDialogVisible: boolean;
  typeAheadSearchTerm: string;
  searchResultItems: SearchResultItem[];
  objectSearchResultsFailed: boolean;
  workspaceSearchResultItems: WorkspaceItem[];
  workspaceSearchResultsLoading: boolean;
  workspaceSearchResultsFailed: boolean;
  workspaceCurrentPageIndex: number;
  workspaceTotalCount: number;
  objectResultsSorting: SearchSortInput | null | undefined;
  viewSearchResultItems: ViewItem[];
  viewSearchResultsLoading: boolean;
  viewSearchResultsFailed: boolean;
  viewCurrentPageIndex: number;
  viewTotalCount: number;
  context: string;
  suggestionsTranslations: { [key: string]: string };
  resultSuggestions: ResultSuggestionsResponse | null;
  combinedSuggestions: SearchSuggestionGroup[];
  contentSelection: ContentSelection | undefined;
  contentSelectionBackup: ContentSelection | undefined;
  selectedObjects: ObjectDto[];
  users: SystemAttributesUser[];
  usersLoading: boolean;
  usersSearchTerm: string;
  smartFiltersHasError: boolean;
  smartFiltersErrorDialogVisibility: boolean;
  smartFiltersForceRetry: boolean;
  smartFiltersRefineQuery: boolean;
  smartSearchSuggestions: SearchSuggestionGroup[];
  authenticatedUser: AuthenticatedUser | undefined;
  deletedObjectIds: UUID[];
  editDataWarningVisible: boolean;
  previousAdvancedFilterForCount: AdvancedFilter;
  scopeFiltersUrlParam: string | undefined;
  typeFiltersUrlParam: string | undefined;
  attributeFiltersUrlParam: string | undefined;
  relationFiltersUrlParam: string | undefined;
  systemFiltersUrlParam: string | undefined;
  searchTermUrlParam: string | undefined;
  pageIndexUrlParam: string | undefined;
  drawerObjectIdUrlParam: string | undefined;
  cypherSearchIdUrlParam: string | undefined;
  cypherSearchId: UUID | null;
  loadedCypherUserQuery: string | null;
  cypherSearchParameters: NlsOverviewParametersMap | null;
  drawerAddToObjectId: string | undefined;
  drawerAddToObjects: ObjectDto[];
};

export const initialSearchState: SearchState = {
  contentOpenDialogVisible: false,
  typeAheadSearchTerm: '',
  searchResultItems: [],
  objectSearchResultsFailed: false,
  workspaceSearchResultItems: [],
  workspaceSearchResultsLoading: false,
  workspaceSearchResultsFailed: false,
  workspaceCurrentPageIndex: 0,
  workspaceTotalCount: 0,
  objectResultsSorting: undefined,
  viewSearchResultItems: [],
  viewSearchResultsLoading: false,
  viewSearchResultsFailed: false,
  viewCurrentPageIndex: 0,
  viewTotalCount: 0,
  context: 'default',
  suggestionsTranslations: {},
  resultSuggestions: { items: [], totalCount: 0 },
  combinedSuggestions: [],
  contentSelection: undefined,
  contentSelectionBackup: undefined,
  selectedObjects: [],
  users: [],
  usersLoading: false,
  usersSearchTerm: '',
  smartFiltersHasError: false,
  smartFiltersErrorDialogVisibility: false,
  smartFiltersForceRetry: false,
  smartFiltersRefineQuery: false,
  smartSearchSuggestions: [],
  authenticatedUser: undefined,
  deletedObjectIds: [],
  editDataWarningVisible: false,
  previousAdvancedFilterForCount: defaultAdvancedFilter,
  scopeFiltersUrlParam: undefined,
  typeFiltersUrlParam: undefined,
  attributeFiltersUrlParam: undefined,
  relationFiltersUrlParam: undefined,
  systemFiltersUrlParam: undefined,
  searchTermUrlParam: undefined,
  pageIndexUrlParam: undefined,
  drawerObjectIdUrlParam: undefined,
  cypherSearchIdUrlParam: undefined,
  cypherSearchId: null,
  loadedCypherUserQuery: null,
  cypherSearchParameters: null,
  drawerAddToObjectId: undefined,
  drawerAddToObjects: [],
};

const searchContainerComponentStoreProps = () => {
  const cancelSmartFiltersLoading = new Subject<boolean>();

  return {
    _cancelSmartFiltersLoadingSubject: cancelSmartFiltersLoading,
    cancelSmartFiltersLoading$: cancelSmartFiltersLoading.asObservable(),
    cancelObjectLoading$: new BehaviorSubject<boolean>(false),
    featureFlagsStore: inject(FeatureFlagsStore),
  };
};

export const SearchContainerComponentStore = signalStore(
  withState(initialSearchState),
  withProps(searchContainerComponentStoreProps),
  withComputed(
    ({
      typeAheadSearchTerm,
      resultSuggestions,
      smartFiltersForceRetry,
      authenticatedUser,
      selectedObjects,
      contentSelection,
      objectResultsSorting,
      featureFlagsStore,
      attributeFiltersUrlParam,
      drawerAddToObjectId,
      drawerAddToObjects,
      cypherSearchParameters,
    }) => {
      const filterGlobalStore = inject(FilterGlobalStore);
      const searchResultsGlobalStore = inject(SearchResultsGlobalStore);
      const populationsGlobalStore = inject(PopulationsGlobalStore);
      const contentDetailsGlobalStore = inject(ContentDetailsGlobalStore);
      const drawerGlobalStore = inject(DrawerGlobalStore);
      const accessControlService = inject(AccessControlService);
      const addObjectsToActionState = toSignal(
        accessControlService.getActionState$('objects', 'addToWorkspace'),
        { initialValue: 'accessible' },
      );
      const addObjectsToNoAccessKey = toSignal(
        accessControlService.getNoAccessKey$('objects', 'addToWorkspace'),
        { initialValue: '' },
      );

      function addObjectsToDisabledBySelection() {
        const selection = contentSelection();
        const selectedItemsCategory = selection?.selectedItemsCategory;
        const noSelection = !selectedItemsCategory;
        const noObjectsFound =
          searchResultsGlobalStore.totalObjectsCount() === 0;
        const nonObjectSelected =
          !!selectedItemsCategory && selectedItemsCategory !== 'object';

        if (featureFlagsStore.addToFrameWithoutSelectionEnabled()) {
          const noSearchTerm = filterGlobalStore.searchTerm() === '';
          const noFiltersForObjects =
            getFiltersForObjectsCount(filterGlobalStore.advancedFilter()) === 0;

          return (
            (noFiltersForObjects && noSearchTerm && noSelection) ||
            noObjectsFound ||
            nonObjectSelected
          );
        }

        return noSelection || noObjectsFound || nonObjectSelected;
      }

      function addObjectsToDisabledByPermissions() {
        return addObjectsToActionState() === 'disabled';
      }

      function editableObjectIdsForEditing() {
        return selectedObjects()
          .filter((object) => !isNotUndefinedOrNull(object.externalId))
          .map((object) => object.id);
      }

      return {
        aiSearchToggleOn: computed(() => filterGlobalStore.aiSearchToggleOn()),
        // Derived so late-arriving attributeCategoryDefinitions (and nlsOverview
        // updates) re-resolve raw tokens instead of leaving them stored forever.
        interactiveNlsOverview: computed<NlsQueryInteractiveOverview | null>(
          () => {
            const parameters = cypherSearchParameters();
            const overviewMarkdown = filterGlobalStore.nlsOverview() ?? '';
            if (!parameters || !overviewMarkdown) {
              return null;
            }
            return nlsQueryOverviewToInteractiveOverview(
              filterGlobalStore.attributeCategoryDefinitions(),
              overviewMarkdown,
              parameters,
            );
          },
        ),
        attributeFiltersUrlContext: computed(() => ({
          attributeFiltersUrlParam: attributeFiltersUrlParam(),
          attributeCategoryDefinitions:
            filterGlobalStore.attributeCategoryDefinitions(),
        })),
        objectResultsColumnCatalogue: computed(() =>
          createObjectResultsColumnCatalogue(
            filterGlobalStore.attributeCategoryDefinitions(),
            filterGlobalStore.resolvedObjectType(),
          ),
        ),
        filterStoreProperties: computed(() => ({
          ...getState(filterGlobalStore),
          appliedFilters: filterGlobalStore.appliedFilters(),
          quickFilterOptions: filterGlobalStore.quickFilterOptions(),
        })),
        searchResultsStoreProperties: computed(() =>
          getState(searchResultsGlobalStore),
        ),
        populationsStoreProperties: computed(() =>
          getState(populationsGlobalStore),
        ),
        contentDetailsStoreProperties: computed(() =>
          getState(contentDetailsGlobalStore),
        ),
        showDrawerFooter: computed(() =>
          contentDetailsGlobalStore.showDrawerFooter(),
        ),
        showDrawerBackdrop: computed(() =>
          contentDetailsGlobalStore.showDrawerBackdrop(),
        ),
        numberOfUnsavedChanges: computed(() =>
          contentDetailsGlobalStore.numberOfUnsavedChanges(),
        ),
        disableCreatePopulationButton: computed(() =>
          populationsGlobalStore.disableCreatePopulationButton(),
        ),
        rightDrawerOpen: computed(
          () => drawerGlobalStore.rightDrawer()?.state === 'in',
        ),
        activeItemId: computed(
          () => contentDetailsGlobalStore.contentItem()?.id ?? '',
        ),
        showWarningMessageForDynamicPopulation: computed(() => {
          const scopeFilters = filterGlobalStore.advancedFilter().scopeFilters;
          return (
            scopeFilters.includes('WORKSPACE') || scopeFilters.includes('VIEW')
          );
        }),
        createPopulationButtonTooltipKey: computed(() =>
          populationsGlobalStore.createPopulationButtonTooltipKey(),
        ),
        searchParams: computed(() => ({
          searchTerm: filterGlobalStore.searchTerm(),
          advancedFilter: filterGlobalStore.advancedFilter(),
          sdkSearchFilter: filterGlobalStore.sdkSearchFilter(),
          sorting: objectResultsSorting(),
        })),
        smartSearchParams: computed(() => ({
          searchTerm: filterGlobalStore.smartSearchQuery(),
          smartFiltersForceRetry: smartFiltersForceRetry(),
        })),
        suggestionParams: computed(() => ({
          typeAheadSearchTerm: typeAheadSearchTerm(),
          appliedFilters: filterGlobalStore.appliedFilters(),
          resultSuggestions: resultSuggestions(),
          aiSearchToggleOn: filterGlobalStore.aiSearchToggleOn(),
          authenticatedUser: authenticatedUser(),
        })),
        smartSuggestionParams: computed(() => ({
          typeAheadSearchTerm: typeAheadSearchTerm(),
          aiSearchToggleOn: filterGlobalStore.aiSearchToggleOn(),
          attributeCategoryDefinitions:
            filterGlobalStore.attributeCategoryDefinitions(),
        })),
        addObjectsToTooltipKey: computed(() =>
          addObjectsToDisabledBySelection() ? 'addObjectsToTooltip' : '',
        ),
        addObjectsToDisabled: computed(
          () =>
            addObjectsToDisabledBySelection() ||
            addObjectsToDisabledByPermissions(),
        ),
        manageWorkspaceActionDisabled: computed(
          () => addObjectsToActionState() === 'disabled',
        ),
        manageWorkspaceNoAccessKey: computed(() => addObjectsToNoAccessKey()),
        elementQuery: computed((): ElementQuery => {
          const drawerAddToObjectIdValue = drawerAddToObjectId();
          const selection = contentSelection();
          const selectedAll =
            selection?.fetchAllItems &&
            (selection.unselectedItems ?? []).length === 0;

          // If there is a drawerAddToObjectId, we are in the flow from the content details drawer,
          // and the element collection should be built from the selected objects in the drawer, not from the search results selection
          if (
            drawerAddToObjects().length > 0 &&
            isUUID(drawerAddToObjectIdValue)
          ) {
            return {
              id: generateUUID(),
              name: 'Untitled',
              objects: drawerAddToObjects(),
              relations: [],
              searchTerm: '',
              advancedFilter: defaultAdvancedFilter,
              selectedIds: [drawerAddToObjectIdValue],
            };
          }

          return {
            id: generateUUID(),
            name: 'Untitled',
            objects: selectedObjects(),
            relations: [],
            searchTerm: filterGlobalStore.searchTerm(),
            advancedFilter: filterGlobalStore.advancedFilter(),
            selectedIds: selectedAll
              ? undefined
              : selectedObjects().map((o) => o.id),
          };
        }),
        searchResultsCountsPerScopeLoading: computed(() =>
          filterGlobalStore.advancedFilterVisible()
            ? false
            : searchResultsGlobalStore.searchResultsLoading() ||
              filterGlobalStore.searchResultsCountLoading(),
        ),
        editableObjectIdsForEditing: computed(() =>
          editableObjectIdsForEditing(),
        ),
        isEditDataDialogRequired: computed(() => {
          const selectedCount = selectedObjects().length;
          const editableCount = editableObjectIdsForEditing().length;
          const hasExternal =
            selectedCount > 0 && editableCount < selectedCount;
          const tooMany = editableCount > OBJECT_SELECTION_LIMIT;
          return hasExternal || tooMany;
        }),
        numberOfCollectionElements: computed(() =>
          drawerAddToObjectId()
            ? 1
            : (contentSelection()?.selectedItemsCount ?? 0),
        ),
      };
    },
  ),
  withMethods(
    (
      store,
      filterGlobalStore = inject(FilterGlobalStore),
      searchResultsGlobalStore = inject(SearchResultsGlobalStore),
      populationsGlobalStore = inject(PopulationsGlobalStore),
      contentDetailsGlobalStore = inject(ContentDetailsGlobalStore),
      searchService = inject(SearchService),
      objectSearchResultsService = inject(ObjectSearchResultsService),
      workspaceSearchResultsService = inject(WorkspaceSearchResultsService),
      viewSearchResultsService = inject(ViewSearchResultsService),
      workspacesService = inject(WorkspacesService),
      usersService = inject(UsersService),
      attributeCategoryDefinitionsService = inject(
        AttributeCategoryDefinitionsService,
      ),
      aiAssistantV2Store = inject(AiAssistantV2Store),
      knowledgeGraphClient = inject(KnowledgeGraphClientBDCore),
      sessionService = inject(SessionService),
      searchSessionService = inject(SearchSessionService),
      router = inject(Router),
      messageService = inject(MessageService),
      analyticsService = inject(ProductAnalyticsService),
      contentOpenService = inject(ContentOpenService),
    ) => {
      function buildSearchUrlQueryParams(
        searchParameters: SearchParameters,
        searchPageIndex: number | undefined,
      ) {
        const cypherSearchId = store.cypherSearchId();
        if (store.aiSearchToggleOn() && cypherSearchId) {
          return cypherSearchToUrlQueryParameters(
            cypherSearchId,
            searchPageIndex,
          );
        }
        if (store.aiSearchToggleOn()) {
          return { version: '1.0.0' };
        }
        return searchParametersToUrlQueryParameters(
          searchParameters,
          searchPageIndex,
        );
      }

      function navigateToSearchUrl(
        searchParameters: SearchParameters,
        searchPageIndex?: number,
      ) {
        router.navigate([baseRoutes.home, 'search'], {
          queryParams: buildSearchUrlQueryParams(
            searchParameters,
            searchPageIndex,
          ),
          replaceUrl: false,
        });
      }

      // Start the cypher search over. The loaded query goes with it, so the
      // same query submitted again counts as a change and runs.
      function resetCypherSearch(): void {
        patchState(store, {
          cypherSearchId: null,
          loadedCypherUserQuery: null,
          cypherSearchParameters: null,
        });
      }

      // Drop the cypher results but keep loadedCypherUserQuery: the smart
      // search filter compares against it to skip a query that just ran.
      function resetCypherResults(): void {
        patchState(store, {
          cypherSearchId: null,
          cypherSearchParameters: null,
        });
      }

      // Armed when something opens the drawer ahead of the search it triggers.
      // The object id, when set, is what that search's results must confirm.
      let keepDrawerOpenIntent:
        | { confirmObjectId: UUID | undefined }
        | undefined;
      // Handed over by prepareSearchResultsLoad, spent on that search's results.
      let confirmDrawerObjectIdInNextResults: UUID | undefined;
      const requestLastOpenedWorkspacesPage$ = new BehaviorSubject<number>(0);
      // Cancels any in-flight view/workspace load so a late keyword response
      // can't repopulate those tabs after they've been cleared (e.g. an AI/NLS
      // submit that never re-triggers the loaders to switchMap-cancel them).
      const cancelViewAndWorkspaceLoading$ = new Subject<void>();

      function updateSmartFiltersHasError(status: boolean) {
        patchState(store, { smartFiltersHasError: status });
      }

      function updateSmartFiltersErrorDialogVisibility(visible: boolean) {
        patchState(store, { smartFiltersErrorDialogVisibility: visible });
      }

      function updateSmartFiltersRefineQuery(smartFiltersRefineQuery: boolean) {
        patchState(store, { smartFiltersRefineQuery });
      }

      function runFilterSmartSearch(searchTerm: string) {
        analyticsService.sendEvent('nls_performed', {
          query: searchTerm,
          mode: 'filters',
        });
        return searchService.smartFilters(searchTerm).pipe(
          tap(({ filters: smartSearchFilters, traceParent, nlsOverview }) => {
            filterGlobalStore.updateFiltersFromSmartSearch(smartSearchFilters);

            searchResultsGlobalStore.updateSearchResultsLoading(false);
            searchResultsGlobalStore.updateSmartFiltersLoading(false);

            filterGlobalStore.updateNlsTraceParent(traceParent);

            filterGlobalStore.updateNlsOverview(
              nlsOverview ?? filterGlobalStore.smartSearchQuery(),
            );

            filterGlobalStore.updateAiSearchResultsActive(true);

            searchResultsGlobalStore.updateCurrentPageIndex(0);
            navigateToSearchUrl(store.searchParams());
          }),
          catchError((error) => {
            console.error('Smart search failed:', error);
            updateSmartFiltersHasError(true);
            updateSmartFiltersErrorDialogVisibility(true);
            return EMPTY;
          }),
          finalize(() => {
            searchResultsGlobalStore.updateSearchResultsLoading(false);
            searchResultsGlobalStore.updateSmartFiltersLoading(false);
          }),
          takeUntil(store.cancelSmartFiltersLoading$),
        );
      }

      function getCypherResultsPage(pageIndex: number): void {
        const cypherSearchId = store.cypherSearchId();
        if (!cypherSearchId) {
          return;
        }

        searchResultsGlobalStore.updateSearchResultsLoading(true);
        searchService
          .runSavedCypherSearch(
            cypherSearchId,
            pageIndex,
            searchResultsGlobalStore.pageSize(),
          )
          .pipe(
            tap((result) => applyCypherResultsPageResponse(result)),
            catchError((error) => {
              notifyInvalidUrlParam(
                'invalidSearchQueryToast.cypherSearchExpiredDetails',
                error,
              );
              return EMPTY;
            }),
            finalize(() => {
              searchResultsGlobalStore.updateSearchResultsLoading(false);
            }),
            takeUntil(store.cancelSmartFiltersLoading$),
          )
          .subscribe();
      }

      function loadSearchResultsPageFromUrl(index: number): void {
        if (store.context() !== 'search') return;

        if (store.cypherSearchIdUrlParam() !== undefined) {
          getCypherResultsPage(index);
          return;
        }

        searchResultsGlobalStore.updateSearchResultsLoading(true);
        objectSearchResultsService.getObjectSearchResultsPage(index);
      }

      function applyCypherResultsPageResponse(
        result: SmartCypherSearchResult,
      ): void {
        patchState(store, {
          searchResultItems: result.results.map(searchResultToSearchResultItem),
        });
        if (result.userQuery !== undefined) {
          patchState(store, { loadedCypherUserQuery: result.userQuery });
          filterGlobalStore.updateSmartSearchQuery(result.userQuery);
        }
        if (result.nlsOverview !== undefined) {
          filterGlobalStore.updateNlsOverview(
            result.nlsOverview ?? filterGlobalStore.smartSearchQuery(),
          );
        }
        if (result.parameters !== undefined) {
          applyCypherSearchResultMeta(result.parameters);
        }
        // Cypher mode only ever returns OBJECT results, so both totals
        // collapse to the same number.
        searchResultsGlobalStore.updateTotalSearchResultsCount(
          result.totalCount,
        );
        searchResultsGlobalStore.updateTotalObjectsCount(result.totalCount);
      }

      function applyCypherSearchResultMeta(
        parameters: NlsOverviewParametersMap,
      ): void {
        patchState(store, { cypherSearchParameters: parameters });
      }

      function runCypherSmartSearch(searchTerm: string) {
        analyticsService.sendEvent('nls_performed', {
          mode: 'cypher',
          query: searchTerm,
        });
        return searchService
          .smartCypherSearch(searchTerm, 0, searchResultsGlobalStore.pageSize())
          .pipe(
            tap((result) => {
              const searchResultItems = result.results.map(
                searchResultToSearchResultItem,
              );

              // Hydrate `loadedCypherUserQuery` so `getSmartSearchFilters`
              // bails when the same searchTerm comes back through the rxMethod.
              patchState(store, {
                searchResultItems,
                cypherSearchId: result.searchId ?? null,
                loadedCypherUserQuery: searchTerm,
              });
              if (result.parameters !== undefined) {
                applyCypherSearchResultMeta(result.parameters);
              }

              // Mirror filter mode: the searchbar shows `smartSearchQuery`
              // when `searchTerm` is empty.
              filterGlobalStore.updateSearchTerm('');

              searchResultsGlobalStore.updateSearchResultsLoading(false);
              searchResultsGlobalStore.updateSmartFiltersLoading(false);
              // Cypher mode only ever returns OBJECT results, so both totals
              // collapse to the same number.
              searchResultsGlobalStore.updateTotalSearchResultsCount(
                result.totalCount,
              );
              searchResultsGlobalStore.updateTotalObjectsCount(
                result.totalCount,
              );

              filterGlobalStore.updateNlsTraceParent(result.traceParent);
              filterGlobalStore.updateNlsOverview(
                result.nlsOverview ?? filterGlobalStore.smartSearchQuery(),
              );
              filterGlobalStore.updateAiSearchResultsActive(true);

              searchResultsGlobalStore.updateCurrentPageIndex(0);
              navigateToSearchUrl(store.searchParams());
            }),
            catchError((error) => {
              console.error('Smart cypher search failed:', error);
              updateSmartFiltersHasError(true);
              updateSmartFiltersErrorDialogVisibility(true);
              return EMPTY;
            }),
            finalize(() => {
              searchResultsGlobalStore.updateSearchResultsLoading(false);
              searchResultsGlobalStore.updateSmartFiltersLoading(false);
            }),
            takeUntil(store.cancelSmartFiltersLoading$),
          );
      }

      function applyCypherSearchFromAssistant(data: CypherSearchData): void {
        updateAiSearchToggleOn(true);
        filterGlobalStore.updateAiSearchResultsActive(true);
        const cypherSearchId = isUUID(data.cypherSearchId)
          ? data.cypherSearchId
          : null;
        patchState(store, {
          cypherSearchId,
          loadedCypherUserQuery: data.userQuery,
        });
        filterGlobalStore.updateSmartSearchQuery(data.userQuery);
        filterGlobalStore.updateNlsOverview(data.nlsOverview ?? data.userQuery);
        filterGlobalStore.updateSearchTerm('');
        navigateToSearchUrl(store.searchParams());
        if (cypherSearchId !== null) {
          getCypherResultsPage(0);
        }
      }

      function applyFilterSearchFromAssistant(data: FilterSearchData): void {
        updateAiSearchToggleOn(true);
        filterGlobalStore.updateAiSearchResultsActive(true);
        resetCypherSearch();
        filterGlobalStore.updateFiltersFromSmartSearch(data.searchParameters);
        filterGlobalStore.updateNlsOverview(
          data.nlsOverview ?? filterGlobalStore.smartSearchQuery(),
        );
        navigateToSearchUrl(store.searchParams());
      }

      function applySemanticSearchFromAssistant(
        data: SemanticSearchData,
      ): void {
        updateAiSearchToggleOn(false);
        filterGlobalStore.updateAiSearchResultsActive(false);
        resetCypherSearch();
        store._cancelSmartFiltersLoadingSubject.next(true);
        filterGlobalStore.updateNlsOverview('');
        filterGlobalStore.updateSearchTerm(data.searchTerm);
        navigateToSearchUrl(store.searchParams());
      }

      function fetchAttributeCategoryDefinitions(
        filterGlobalStore: FilterGlobalStore,
      ) {
        attributeCategoryDefinitionsService
          .getAttributeCategoryDefinitions()
          .pipe(take(1))
          .subscribe((attributeCategoryDefinitions) => {
            filterGlobalStore.updateAttributeCategoryDefinitions(
              attributeCategoryDefinitions,
            );
          });
      }

      function updateTypeAheadSearchTerm(typeAheadSearchTerm: string) {
        patchState(store, {
          typeAheadSearchTerm,
        });
      }

      function updateContentOpenDialogVisible(
        contentOpenDialogVisible: boolean,
      ) {
        patchState(store, {
          contentOpenDialogVisible,
        });
        if (!contentOpenDialogVisible) {
          patchState(store, {
            drawerAddToObjectId: undefined,
            drawerAddToObjects: [],
          });
        }
      }

      function updateContentSelection(
        contentSelection: ContentSelection | undefined,
      ) {
        patchState(store, {
          contentSelection: cloneDeep(contentSelection),
        });
      }

      function updateContentSelectionBackup(
        contentSelectionBackup: ContentSelection | undefined,
      ) {
        patchState(store, {
          contentSelectionBackup: cloneDeep(contentSelectionBackup),
        });
      }

      function updateSelectedElements(
        cancelObjectLoading$?: Observable<boolean>,
      ): Observable<ObjectDto[]> {
        const contentSelection = store.contentSelection();
        return getSelectedContent(
          searchService,
          contentSelection,
          searchResultsGlobalStore,
          filterGlobalStore,
          store.cypherSearchId(),
          cancelObjectLoading$,
        ).pipe(
          // avoid outdated updates
          filter(() => store.contentSelection() === contentSelection),
          tap((selectedObjects) => patchState(store, { selectedObjects })),
        );
      }

      function notifyAddToWorkspaceFromAssistantFailed(
        error: unknown,
      ): Observable<never> {
        console.error('Add to workspace from assistant failed:', error);
        messageService.add({
          severity: 'error',
          summary: 'addToWorkspaceErrorToast.title',
          detail: 'addToWorkspaceErrorToast.details',
          data: { tScope: 'featureSearch' },
        });
        return EMPTY;
      }

      function navigateToManageData(objectIds: UUID[]) {
        router.navigate([baseRoutes.manageData], {
          state: { objectIds },
        });
        analyticsService.sendEvent('bulk_edit_data_started', {
          objects_count: objectIds.length,
        });
      }

      function advancedFilterCountHandler(
        counts: Map<SearchScope, number>,
        advancedFilter: AdvancedFilter,
      ) {
        filterGlobalStore.updateSearchResultsCountInAdvancedFilter(
          getFilteredResultsCount(advancedFilter.scopeFilters, counts),
        );

        filterGlobalStore.updateSearchResultsCountLoading(false);
      }

      let selectedObjectIds: UUID[] | null = null;

      async function fetchElementCollectionForIds(
        ids: UUID[],
      ): Promise<ElementCollection | null> {
        if (ids.length === 0) {
          return null;
        }
        const objects = await knowledgeGraphClient
          .getObjects({ filter: { ids } })
          .getAll();
        return objects.length > 0
          ? { objects: [...objects], relations: [] }
          : null;
      }

      async function fetchSearchElementCollection(): Promise<ElementCollection | null> {
        const cypherSearchId = store.cypherSearchId();
        const results$ = cypherSearchId
          ? searchService.fetchAllObjectsFromCypherSearch(
              cypherSearchId,
              searchResultsGlobalStore.totalObjectsCount(),
            )
          : searchService.fetchAllObjectsWithCurrentFilters({
              ...store.searchParams(),
              withAttributes: false,
            });
        const results = await firstValueFrom(results$);
        if (!results) {
          return null;
        }
        const objectIds = results
          .filter((r) => r.scope === 'OBJECT')
          .map((r) => r.id);
        if (objectIds.length === 0) {
          return null;
        }
        const objects = await knowledgeGraphClient
          .getObjects({ filter: { ids: objectIds } })
          .getAll();
        return { objects: [...objects], relations: [] };
      }

      function searchResultsCountHandler(
        counts: Map<SearchScope, number>,
        scopeFilters: SearchScope[],
      ) {
        filterGlobalStore.updateSearchResultsCountInAdvancedFilter(
          getFilteredResultsCount(scopeFilters, counts),
        );
        filterGlobalStore.updateSearchResultsCountLoading(false);

        filterGlobalStore.updateSearchResultsCountPerScope(counts);

        searchResultsGlobalStore.updateTotalSearchResultsCount(
          filterGlobalStore.searchResultsCountInAdvancedFilter(),
        );

        searchResultsGlobalStore.updateTotalObjectsCount(
          getFilteredResultsCount(['OBJECT'], counts),
        );

        if (
          store.context() === 'search' &&
          searchResultsGlobalStore.totalSearchResultsCount() === 0
        ) {
          const activeQuery = store.aiSearchToggleOn()
            ? filterGlobalStore.smartSearchQuery()
            : filterGlobalStore.searchTerm();
          analyticsService.sendEvent('no_results_search', {
            search_term: activeQuery,
          });
        }

        selectedObjectIds = null;

        if (store.context() === 'search') {
          aiAssistantV2Store.updateContext({
            type: 'search',
            query: filterGlobalStore.searchTerm(),
            resultCount: searchResultsGlobalStore.totalSearchResultsCount(),
            appliedFilters: serializeFilterLabels(
              filterGlobalStore.appliedFilters(),
            ),
            initialSuggestions: SEARCH_INITIAL_SUGGESTIONS,
          });
          aiAssistantV2Store.reloadInsightSuggestions();
          aiAssistantV2Store.invalidateContext();
        }
      }

      function objectSearchResultsCountHandler(totalCount: number) {
        searchResultsGlobalStore.updateTotalObjectsCount(totalCount);
      }

      function exitAiSearchModeIfActive() {
        // Manually picking a filter leaves AI mode; otherwise the NLS OBJECT-scope override ignores the chosen scope.
        if (store.aiSearchToggleOn()) {
          updateAiSearchToggleOn(false);
        }
      }

      function addScopeFilter(selectedScopeFilter: SearchScope) {
        exitAiSearchModeIfActive();

        filterGlobalStore.updateAdvancedFilter({
          ...filterGlobalStore.advancedFilter(),
          scopeFilters: getSortedScopeFilters(
            [
              ...filterGlobalStore.advancedFilter().scopeFilters,
              selectedScopeFilter,
            ],
            filterGlobalStore.defaultScopeFilterOptions(),
          ),
        });

        searchResultsGlobalStore.updateCurrentPageIndex(0);
        navigateToSearchUrl(store.searchParams());
      }

      function updateSearchResultsCount() {
        handleCountSearchResults(
          filterGlobalStore.advancedFilter(),
          filterGlobalStore,
          searchService,
          (counts, advancedFilter) =>
            searchResultsCountHandler(counts, advancedFilter.scopeFilters),
        );
      }

      function updateAiSearchToggleOn(aiSearchToggleOn: boolean) {
        // Only allow AI search when the feature flag is enabled
        const aiSearchToggleOnValue =
          store.featureFlagsStore.aiSearchEnabled() && aiSearchToggleOn;

        filterGlobalStore.updateAiSearchToggleOn(aiSearchToggleOnValue);

        searchSessionService.setAiSearchToggleOnToSession(
          aiSearchToggleOnValue,
        );
      }

      function notifyInvalidUrlParam(detailKey: string, error: unknown): void {
        console.error('Invalid search url parameter:', detailKey, error);
        messageService.add({
          severity: 'error',
          summary: 'invalidSearchQueryToast.title',
          detail: detailKey,
          data: { tScope: 'featureSearch' },
        });
      }

      // The url is fine here: the object itself is gone or out of reach, so
      // this must not blame the query parameters the way the toast above does.
      function notifySharedObjectUnavailable(): void {
        messageService.add({
          severity: 'warn',
          summary: 'sharedObjectUnavailableToast.title',
          detail: 'sharedObjectUnavailableToast.details',
          data: { tScope: 'featureSearch' },
        });
      }

      function notifySharedObjectNotInResults(): void {
        messageService.add({
          severity: 'warn',
          summary: 'sharedObjectNotInResultsToast.title',
          detail: 'sharedObjectNotInResultsToast.details',
          data: { tScope: 'featureSearch' },
        });
      }

      // Pipes parameter edits through a single switchMap so concurrent edits
      // (a) read the freshly-patched state when computing the next overrides
      // and (b) cancel any in-flight refetch instead of racing each other.
      const applyCypherSearchParameterUpdate =
        rxMethod<NlsOverviewParametersMap>(
          pipe(
            tap((nextParameters) => {
              patchState(store, { cypherSearchParameters: nextParameters });
              searchResultsGlobalStore.updateCurrentPageIndex(0);
              searchResultsGlobalStore.updateSearchResultsLoading(true);
            }),
            switchMap((nextParameters) => {
              const cypherSearchId = store.cypherSearchId();
              if (!cypherSearchId) {
                searchResultsGlobalStore.updateSearchResultsLoading(false);
                return EMPTY;
              }
              return searchService
                .runSavedCypherSearch(
                  cypherSearchId,
                  0,
                  searchResultsGlobalStore.pageSize(),
                  nextParameters,
                )
                .pipe(
                  tap((result) => applyCypherResultsPageResponse(result)),
                  catchError((error) => {
                    notifyInvalidUrlParam(
                      'invalidSearchQueryToast.cypherSearchExpiredDetails',
                      error,
                    );
                    return EMPTY;
                  }),
                  finalize(() => {
                    searchResultsGlobalStore.updateSearchResultsLoading(false);
                  }),
                );
            }),
          ),
        );

      function notifySearchResultsFailed(detailKey: string): void {
        messageService.add({
          severity: 'error',
          summary: 'searchResultsErrorToast.title',
          detail: detailKey,
          data: { tScope: 'featureSearch' },
        });
      }

      // Cypher load owns the results when the URL carries a searchId.
      function canLoadSearchResults(): boolean {
        return (
          store.context() === 'search' &&
          !searchResultsGlobalStore.smartFiltersLoading() &&
          !isNotUndefinedOrNull(store.cypherSearchIdUrlParam())
        );
      }

      function prepareSearchResultsLoad(): void {
        updateTypeAheadSearchTerm('');
        const intent = keepDrawerOpenIntent;
        keepDrawerOpenIntent = undefined;
        confirmDrawerObjectIdInNextResults = intent?.confirmObjectId;
        closeDetailsDrawerForSearchParameterChange(intent !== undefined);
        searchResultsGlobalStore.updateSearchResultsLoading(true);
        searchResultsGlobalStore.updateIsSelectionMode(false);
        if (!store.aiSearchToggleOn()) {
          filterGlobalStore.updateAiSearchResultsActive(false);
          resetCypherResults();
        }
      }

      function trackSearchPerformed(searchTerm: string): void {
        const {
          scopeFilters,
          elementTypeFilters,
          attributeFilters,
          relationFilters,
          systemAttributesFilter,
        } = filterGlobalStore.advancedFilter();
        analyticsService.sendEvent('search_performed', {
          search_term: searchTerm,
          object_types: elementTypeFilters,
          category_filter_count: scopeFilters.length,
          attribute_filter_count: attributeFilters.length,
          relation_filter_count: relationFilters.length,
          people_filter_count: systemAttributesFilter
            ? Object.values(systemAttributesFilter).filter(
                (value) => value !== undefined,
              ).length
            : 0,
        });
      }

      function applyObjectSearchResultItems(
        resultItems: SearchResultItem[],
      ): void {
        const deletedObjectIds = new Set(store.deletedObjectIds());

        patchState(store, {
          searchResultItems: resultItems.map((item) =>
            item.scope === 'object' &&
            isUUID(item.id) &&
            deletedObjectIds.has(item.id)
              ? { ...item, deleted: true }
              : item,
          ),
        });

        searchResultsGlobalStore.updateSearchResultsLoading(false);
      }

      function confirmDrawerObjectInSearchResults(
        resultItems: SearchResultItem[],
        searchFailed: boolean,
      ): void {
        const objectId = confirmDrawerObjectIdInNextResults;
        // Spent either way: left set, it would judge a later, unrelated search.
        confirmDrawerObjectIdInNextResults = undefined;
        if (objectId === undefined || searchFailed) {
          return;
        }

        // Nothing to close or explain: the drawer was dismissed mid-search,
        // or it already moved on to an item this search says nothing about.
        if (
          !contentDetailsGlobalStore.drawerState().visible ||
          contentDetailsGlobalStore.contentItem()?.id !== objectId
        ) {
          return;
        }

        const isInResults = resultItems.some(
          (item) => item.scope === 'object' && item.id === objectId,
        );
        if (isInResults) {
          return;
        }

        contentDetailsGlobalStore.updateIsDrawerOpen(false);
        notifySharedObjectNotInResults();
      }

      function getObjectSearchResultItemsPage(index: number): void {
        searchResultsGlobalStore.updateSearchResultsLoading(true);
        objectSearchResultsService.getObjectSearchResultsPage(index);
        searchResultsGlobalStore.updateCurrentPageIndex(index);

        navigateToSearchUrl(store.searchParams(), index + 1);
      }

      function updateObjectResultsSorting(
        sorting: SearchSortInput | undefined,
      ): void {
        patchState(store, { objectResultsSorting: sorting ?? null });
        searchResultsGlobalStore.updateCurrentPageIndex(0);
        navigateToSearchUrl(store.searchParams(), 1);
      }

      // "Default" search (no term, no filters) lists workspaces like the
      // workspaces overview: most recently opened first, which cannot be
      // ordered server-side because last-opened is per-user activity data.
      function isDefaultSearch(searchTerm: string): boolean {
        return (
          !isNotBlank(searchTerm) &&
          filterGlobalStore.appliedFilters().length === 0
        );
      }

      // Mirrors paginatedSearchResults$: page requests ride the subject so a newer
      // page cancels the in-flight one, and failures emit fallbacks to keep it alive.
      function lastOpenedWorkspacesPages$(limit: number | undefined) {
        requestLastOpenedWorkspacesPage$.next(0);
        return requestLastOpenedWorkspacesPage$.pipe(
          switchMap((pageIndex) =>
            workspacesService
              .getWorkspaceItems({
                orderBy: 'lastOpened',
                pageSize: limit ?? searchResultsGlobalStore.pageSize(),
                pageNumber: pageIndex + 1,
              })
              .pipe(
                map((page) => ({
                  workspaceSearchResultItems: [...page.results],
                  totalCount: page.totalCount,
                  error: undefined as unknown,
                })),
                catchError((error: unknown) =>
                  of({
                    workspaceSearchResultItems: [],
                    totalCount: 0,
                    error,
                  }),
                ),
              ),
          ),
        );
      }

      function getWorkspaceSearchResultItemsPage(index: number): void {
        patchState(store, {
          workspaceSearchResultsLoading: true,
          workspaceCurrentPageIndex: index,
        });
        if (isDefaultSearch(filterGlobalStore.searchTerm())) {
          requestLastOpenedWorkspacesPage$.next(index);
          return;
        }
        workspaceSearchResultsService.getWorkspaceSearchResultsPage(index);
      }

      function getViewSearchResultItemsPage(index: number): void {
        patchState(store, { viewSearchResultsLoading: true });
        viewSearchResultsService.getViewSearchResultsPage(index);
        patchState(store, { viewCurrentPageIndex: index });
      }

      function closeDetailsDrawerForSearchParameterChange(
        keepDetailsDrawerOpen: boolean,
      ): void {
        if (keepDetailsDrawerOpen) {
          patchState(store, { drawerObjectIdUrlParam: undefined });
          return;
        }

        if (contentDetailsGlobalStore.drawerState().visible) {
          patchState(store, { drawerObjectIdUrlParam: undefined });
        }

        contentDetailsGlobalStore.updateIsDrawerOpen(false);
      }

      function applyDrawerObjectIdUrlParams(
        drawerObjectIdUrlParam: string,
      ): void {
        patchState(store, {
          scopeFiltersUrlParam: undefined,
          typeFiltersUrlParam: undefined,
          attributeFiltersUrlParam: undefined,
          relationFiltersUrlParam: undefined,
          systemFiltersUrlParam: undefined,
          searchTermUrlParam: undefined,
          pageIndexUrlParam: undefined,
          drawerObjectIdUrlParam,
          cypherSearchIdUrlParam: undefined,
        });
      }

      function applySearchQueryUrlParams(parameters: Params): void {
        patchState(store, {
          scopeFiltersUrlParam: parameters['scopeFilters'],
          typeFiltersUrlParam: parameters['typeFilters'],
          attributeFiltersUrlParam: parameters['attributeFilters'],
          relationFiltersUrlParam: parameters['relationFilters'],
          systemFiltersUrlParam: parameters['systemFilters'],
          searchTermUrlParam: parameters['searchTerm'],
          pageIndexUrlParam: parameters['pageIndex'],
          drawerObjectIdUrlParam: undefined,
          cypherSearchIdUrlParam: parameters['cypherSearchId'],
        });
      }

      const loadObjectSearchResultItems = rxMethod<ObjectSearchLoadRequest>(
        pipe(
          tap(({ loadMode }) => {
            // A dropped load never reaches prepareSearchResultsLoad, so release
            // the intent here or it outlives the search it was for.
            if (loadMode === 'prepare' && !canLoadSearchResults()) {
              keepDrawerOpenIntent = undefined;
            }
          }),
          filter(canLoadSearchResults),
          tap(({ loadMode }) => {
            if (loadMode === 'prepare') {
              prepareSearchResultsLoad();
            }
            patchState(store, { objectSearchResultsFailed: false });
          }),
          switchMap(({ searchParams }) =>
            objectSearchResultsService
              .paginatedObjectSearch$(
                {
                  ...searchParams,
                  withUsedInViews: true,
                  // TODO: pass attributeCategoryIds
                  withAttributes: true,
                },
                searchResultsGlobalStore.currentPageIndex(),
                store.aiSearchToggleOn(),
              )
              .pipe(
                tap(({ results, totalCount, error }) => {
                  if (error !== undefined) {
                    notifySearchResultsFailed(
                      'searchResultsErrorToast.objectsDetails',
                    );
                  }
                  patchState(store, {
                    objectSearchResultsFailed: error !== undefined,
                  });
                  objectSearchResultsCountHandler(totalCount);
                  applyObjectSearchResultItems(results.resultItems);
                  confirmDrawerObjectInSearchResults(
                    results.resultItems,
                    error !== undefined,
                  );
                }),
                catchError(() => {
                  confirmDrawerObjectIdInNextResults = undefined;
                  notifySearchResultsFailed(
                    'searchResultsErrorToast.objectsDetails',
                  );
                  patchState(store, { objectSearchResultsFailed: true });
                  searchResultsGlobalStore.updateSearchResultsLoading(false);
                  return EMPTY;
                }),
              ),
          ),
        ),
      );

      return {
        fetchAuthenticatedUser: () => {
          sessionService
            .getAuthenticatedUser()
            .pipe(
              takeUntilDestroyed(),
              tap((authenticatedUser) => {
                patchState(store, { authenticatedUser });
              }),
            )
            .subscribe();
        },
        // Methods for content details
        updateIsDrawerOpen: (isDrawerOpen: boolean) => {
          contentDetailsGlobalStore.updateIsDrawerOpen(isDrawerOpen);
        },
        updateScrollToUsedIn: (scrollToUsedIn: boolean) => {
          contentDetailsGlobalStore.updateScrollToUsedIn(scrollToUsedIn);
        },
        showUnsavedPublishedItemWarning: () => {
          contentDetailsGlobalStore.updateShowUnsavedPublishedItemWarning(true);
        },
        updateContentItem: (searchResultItem: SearchResultItem | undefined) => {
          contentDetailsGlobalStore.updateContentItem(
            searchResultItem
              ? searchResultItemToContentItem(searchResultItem)
              : undefined,
          );
        },
        openWorkspaceDetails: (workspaceItem: WorkspaceItem) => {
          contentDetailsGlobalStore.updateContentItem(
            workspaceItemToContentItem(workspaceItem),
          );
          contentDetailsGlobalStore.updateIsDrawerOpen(true);
        },
        openViewDetails: (viewItem: ViewItem) => {
          contentDetailsGlobalStore.updateContentItem(
            viewItemToContentItem(viewItem),
          );
          contentDetailsGlobalStore.updateIsDrawerOpen(true);
        },
        updateDrawerAddToObjectId: (
          drawerAddToObjectId: string | undefined,
        ) => {
          patchState(store, { drawerAddToObjectId });
        },
        updateAiSearchToggleOn: (aiSearchToggleOn: boolean) => {
          updateAiSearchToggleOn(aiSearchToggleOn);
        },
        handleDeletedObject: (deletedObjectId: UUID): void => {
          if (store.deletedObjectIds().includes(deletedObjectId)) {
            return;
          }

          const deletedObjectIds = [
            ...store.deletedObjectIds(),
            deletedObjectId,
          ];
          patchState(store, {
            deletedObjectIds,
            searchResultItems: store
              .searchResultItems()
              .map((item) =>
                item.id === deletedObjectId ? { ...item, deleted: true } : item,
              ),
          });
        },
        // Methods for Searchbar and Suggestions
        handleSearch: (searchTerm: string): void => {
          patchState(store, { loadedCypherUserQuery: null });
          if (store.aiSearchToggleOn()) {
            filterGlobalStore.updateSmartSearchQuery(searchTerm);
          } else {
            filterGlobalStore.updateSearchTerm(searchTerm);
          }

          searchResultsGlobalStore.updateCurrentPageIndex(0);
          navigateToSearchUrl(store.searchParams());
        },
        updateOverviewAttribute(update: AttributeValueWithParameterKey): void {
          const currentParameters = store.cypherSearchParameters();
          if (!currentParameters) return;
          const existing = currentParameters[update.parameterKey];
          if (!existing) return;
          const patch = attributeDataPayloadToParameterUpdate(
            update.attribute,
            filterGlobalStore.attributeCategoryDefinitions(),
          );
          applyCypherSearchParameterUpdate({
            ...currentParameters,
            [update.parameterKey]: { ...existing, ...patch },
          });
        },
        updateOverviewSimpleValue(update: SimpleValueWithParameterKey): void {
          const currentParameters = store.cypherSearchParameters();
          if (!currentParameters) return;
          const existing = currentParameters[update.parameterKey];
          if (!existing) return;
          applyCypherSearchParameterUpdate({
            ...currentParameters,
            [update.parameterKey]: { ...existing, currentValue: update.value },
          });
        },
        handleObjectSuggestion: (suggestion: ResultSuggestionItem): void => {
          analyticsService.sendEvent('result_suggestion_clicked', {
            search_term: store.typeAheadSearchTerm(),
            filters_active: filterGlobalStore.appliedFilters().length > 0,
          });
          contentDetailsGlobalStore.updateContentItem(
            searchResultItemToContentItem(suggestion.searchResultItem),
          );
          contentDetailsGlobalStore.updateIsDrawerOpen(true);
          keepDrawerOpenIntent = { confirmObjectId: undefined };
          filterGlobalStore.updateSearchTerm(suggestion.name);
          searchResultsGlobalStore.updateCurrentPageIndex(0);
          navigateToSearchUrl(store.searchParams());
        },
        handleSuggestionFilter: (suggestionFilter: FilterItem) => {
          if (suggestionFilter.selected) {
            analyticsService.sendEvent('filter_suggestion_clicked');
          }
          if (
            isObjectFilterItemSelected(suggestionFilter) &&
            !filterGlobalStore.advancedFilter().scopeFilters.includes('OBJECT')
          ) {
            addScopeFilter('OBJECT');
          }

          filterGlobalStore.updateAdvancedFilter(
            getUpdatedAdvancedFilter(
              getUpdatedSuggestionFilters(
                filterGlobalStore.appliedFilters(),
                suggestionFilter,
              ),
              filterGlobalStore.advancedFilter(),
            ),
          );

          searchResultsGlobalStore.updateCurrentPageIndex(0);
          navigateToSearchUrl(store.searchParams());
        },
        applySmartSuggestion: (suggestion: SmartSuggestionItem) => {
          patchState(store, { smartSearchSuggestions: [] });
          filterGlobalStore.updateSmartSearchQuery(suggestion.query ?? '');
          searchResultsGlobalStore.updateCurrentPageIndex(0);
          navigateToSearchUrl(store.searchParams());
        },
        clearSearchInput: () => {
          if (store.aiSearchToggleOn()) {
            filterGlobalStore.updateSmartSearchQuery('');
            return;
          }
          filterGlobalStore.updateSearchTerm('');
          filterGlobalStore.updateSmartSearchQuery('');

          searchResultsGlobalStore.updateCurrentPageIndex(0);
          navigateToSearchUrl(store.searchParams());
        },
        clearSearch: (navigateToHome?: boolean) => {
          updateTypeAheadSearchTerm('');
          patchState(store, {
            workspaceCurrentPageIndex: 0,
            workspaceSearchResultItems: [],
            workspaceSearchResultsLoading: false,
            workspaceTotalCount: 0,
            viewCurrentPageIndex: 0,
            viewSearchResultItems: [],
            viewSearchResultsLoading: false,
            viewTotalCount: 0,
            cypherSearchId: null,
            loadedCypherUserQuery: null,
          });
          filterGlobalStore.clearAll();
          if (navigateToHome) {
            filterGlobalStore.updateSearchTerm('');
            updateSearchResultsCount();

            router.navigate([baseRoutes.home]);
          }
        },
        getResultSuggestions: rxMethod<string>(
          pipe(
            filter(() => !store.aiSearchToggleOn()),
            tap(() =>
              patchState(store, {
                resultSuggestions: null,
              }),
            ),
            debounceTime(200),
            distinctUntilChanged(),
            switchMap((typeAheadSearchTerm) =>
              getSearchSuggestions(
                typeAheadSearchTerm,
                searchService,
                filterGlobalStore.advancedFilter(),
              ).pipe(
                tap((resultSuggestions) => {
                  patchState(store, {
                    resultSuggestions,
                  });
                  if (isNotBlank(typeAheadSearchTerm)) {
                    analyticsService.sendEvent('result_suggestions_shown', {
                      search_term: typeAheadSearchTerm,
                      filters_active:
                        filterGlobalStore.appliedFilters().length > 0,
                      result_count: resultSuggestions.items.length,
                    });
                  }
                }),
              ),
            ),
          ),
        ),
        getSmartSearchSuggestions: rxMethod<SmartSuggestionParameters>(
          pipe(
            filter(() => store.aiSearchToggleOn()),
            distinctUntilChanged(),
            switchMap(({ typeAheadSearchTerm, attributeCategoryDefinitions }) =>
              searchService
                .searchSmartSuggestions(
                  typeAheadSearchTerm,
                  attributeCategoryDefinitions,
                )
                .pipe(
                  tap((suggestionItems) =>
                    patchState(store, {
                      smartSearchSuggestions:
                        getSmartSearchSuggestionGroup(suggestionItems),
                    }),
                  ),
                ),
            ),
          ),
        ),
        getCombinedSuggestions: rxMethod<SuggestionParameters>(
          pipe(
            filter(() => !store.aiSearchToggleOn()),
            distinctUntilChanged(),
            switchMap(
              ({ typeAheadSearchTerm, appliedFilters, resultSuggestions }) =>
                getCombinedSuggestions(
                  typeAheadSearchTerm,
                  appliedFilters,
                  resultSuggestions,
                  store.authenticatedUser(),
                  searchService,
                ).pipe(
                  tap((combinedSuggestions) =>
                    patchState(store, {
                      combinedSuggestions,
                    }),
                  ),
                ),
            ),
          ),
        ),
        updateTypeAheadSearchTerm: (typeAheadSearchTerm: string) => {
          updateTypeAheadSearchTerm(typeAheadSearchTerm);
        },
        updateContext: (context: string) => {
          patchState(store, {
            context,
          });
          if (context === 'search') {
            const buildSearchContext = () => ({
              type: 'search' as const,
              query: filterGlobalStore.searchTerm(),
              resultCount: searchResultsGlobalStore.totalSearchResultsCount(),
              // "Select all" nulls selectedObjectIds, so read the effective
              // count from contentSelection; 0 tells the agent nothing is selected
              selectionCount: store.contentSelection()?.selectedItemsCount ?? 0,
              appliedFilters: serializeFilterLabels(
                filterGlobalStore.appliedFilters(),
              ),
              nlsOverview: filterGlobalStore.nlsOverview() || undefined,
              initialSuggestions: SEARCH_INITIAL_SUGGESTIONS,
            });
            const buildSearchContextWithElements = async () => {
              const base = buildSearchContext();

              // When objects are selected, prioritize the selection
              if (selectedObjectIds !== null && selectedObjectIds.length > 0) {
                const ids = selectedObjectIds;
                const baseWithSelection = {
                  ...base,
                  selectionCount: ids.length,
                  selectedIds: ids,
                };
                try {
                  const collection = await fetchElementCollectionForIds(ids);
                  if (collection === null) {
                    return baseWithSelection;
                  }
                  return {
                    ...baseWithSelection,
                    elements: collection,
                    insightSignal: buildInsightSignal(
                      toElementSummaryItems(collection),
                    ),
                  };
                } catch {
                  return baseWithSelection;
                }
              }

              const objectCount = searchResultsGlobalStore.totalObjectsCount();
              if (objectCount === 0 || objectCount > 500) {
                return base;
              }
              try {
                const collection = await fetchSearchElementCollection();
                if (collection === null || collection.objects.length === 0) {
                  return base;
                }
                return {
                  ...base,
                  elements: collection,
                  insightSignal: buildInsightSignal(
                    toElementSummaryItems(collection),
                  ),
                };
              } catch {
                return base;
              }
            };
            aiAssistantV2Store.setContextBuilder(
              buildSearchContextWithElements,
              buildSearchContext(),
            );
          } else if (context === 'default') {
            aiAssistantV2Store.setContextBuilder(null);
          }
        },
        // Methods for QuickFilters
        addScopeFilter: (selectedScopeFilter: SearchScope) => {
          addScopeFilter(selectedScopeFilter);
        },
        updateSearchResultsCount: () => {
          updateSearchResultsCount();
        },
        // AI/NLS search is object-only; used to drop stale view/workspace
        // results so those tabs don't show leftover keyword-search matches.
        clearViewAndWorkspaceSearchResults: () => {
          // Cancel any in-flight keyword view/workspace load first, or its late
          // response would repopulate the tabs we're about to clear.
          cancelViewAndWorkspaceLoading$.next();
          patchState(store, {
            viewSearchResultItems: [],
            viewSearchResultsLoading: false,
            viewSearchResultsFailed: false,
            viewCurrentPageIndex: 0,
            viewTotalCount: 0,
            workspaceSearchResultItems: [],
            workspaceSearchResultsLoading: false,
            workspaceSearchResultsFailed: false,
            workspaceCurrentPageIndex: 0,
            workspaceTotalCount: 0,
          });
        },
        updateAdvancedFilterVisible: (visible: boolean) => {
          if (visible) {
            filterGlobalStore.updateSearchResultsCountInAdvancedFilter(
              searchResultsGlobalStore.totalSearchResultsCount(),
            );
            fetchAttributeCategoryDefinitions(filterGlobalStore);
          }
          filterGlobalStore.updateAdvancedFilterVisible(visible);
        },
        reloadSearchAfterFilterChange: (): void => {
          exitAiSearchModeIfActive();

          searchResultsGlobalStore.updateCurrentPageIndex(0);
          navigateToSearchUrl(store.searchParams());
        },
        clearFilters: () => {
          // A smart search still in flight lands on updateFiltersFromSmartSearch
          // and puts back both the filters and the overview, so stop it first.
          store._cancelSmartFiltersLoadingSubject.next(true);
          // Leaving the cypher metadata for the next results load to clear lets
          // fetchSearchElementCollection pull from the old AI search meanwhile.
          resetCypherSearch();
          updateAiSearchToggleOn(false);
          filterGlobalStore.updateAiSearchResultsActive(false);
          filterGlobalStore.updateSearchTerm('');
          // Leaving the AI query behind makes resubmitting it a no-op, because
          // the smart search only runs when the query changes.
          filterGlobalStore.updateSmartSearchQuery('');
          filterGlobalStore.clearAllFilters();
          // A refine-query prompt left standing keeps focusing the searchbar,
          // and a stale error decides which term it shows.
          updateSmartFiltersHasError(false);
          updateSmartFiltersErrorDialogVisibility(false);
          updateSmartFiltersRefineQuery(false);
          searchResultsGlobalStore.updateCurrentPageIndex(0);
          navigateToSearchUrl(store.searchParams());
        },
        // Methods for AdvancedFilter
        updateSelectedAdvancedFilter: (
          selectedAdvancedFilter: AdvancedFilter,
        ) => {
          exitAiSearchModeIfActive();

          filterGlobalStore.updateAdvancedFilter(selectedAdvancedFilter);

          searchResultsGlobalStore.updateCurrentPageIndex(0);
          navigateToSearchUrl(store.searchParams());
        },
        onAdvancedFilterForCountChanged: (
          advancedFilterForCount: AdvancedFilter,
        ) => {
          if (
            isObjectFilterAddedOrUpdated(
              advancedFilterForCount,
              store.previousAdvancedFilterForCount(),
            ) &&
            !advancedFilterForCount.scopeFilters.includes('OBJECT')
          ) {
            filterGlobalStore.updateAutoSelectObjectScope(true);
          } else {
            filterGlobalStore.updateAutoSelectObjectScope(false);
          }

          patchState(store, {
            previousAdvancedFilterForCount: cloneDeep(advancedFilterForCount),
          });

          handleCountSearchResults(
            advancedFilterForCount,
            filterGlobalStore,
            searchService,
            advancedFilterCountHandler,
          );
        },
        updateCancelSmartFiltersLoading: (
          cancelSmartFiltersLoading: boolean,
        ): void => {
          searchResultsGlobalStore.updateSearchResultsLoading(false);
          searchResultsGlobalStore.updateSmartFiltersLoading(false);
          store._cancelSmartFiltersLoadingSubject.next(
            cancelSmartFiltersLoading,
          );
        },
        // Methods for SearchResults
        updateIsSelectionMode: (isSelectionMode: boolean): void => {
          searchResultsGlobalStore.updateIsSelectionMode(isSelectionMode);
        },
        showContentOpen: (selection: ContentSelection) => {
          patchState(store, {
            drawerAddToObjectId: undefined,
            drawerAddToObjects: [],
          });
          updateContentSelectionBackup(store.contentSelection());
          updateContentSelection({
            selectedItems: selection.selectedItems,
            fetchAllItems: selection.fetchAllItems ?? false,
            unselectedItems: selection.unselectedItems ?? [],
            selectedItemsCategory: selection.selectedItemsCategory ?? 'object',
            selectedItemsCount:
              selection.selectedItemsCount ?? selection.selectedItems.length,
          });
          updateContentOpenDialogVisible(true);
        },
        updateEditDataLoading: (loading: boolean): void => {
          searchResultsGlobalStore.updateEditDataLoading(loading);
        },
        editDataForObjects: (
          selection: ContentSelection,
          cancelObjectLoading$?: BehaviorSubject<boolean>,
        ) => {
          searchResultsGlobalStore.updateEditDataLoading(true);

          updateContentSelection({
            selectedItems: selection.selectedItems,
            fetchAllItems: selection.fetchAllItems ?? false,
            unselectedItems: selection.unselectedItems ?? [],
            selectedItemsCategory: selection.selectedItemsCategory ?? 'object',
            selectedItemsCount:
              selection.selectedItemsCount ?? selection.selectedItems.length,
          });

          cancelObjectLoading$?.next(false);
          updateSelectedElements(cancelObjectLoading$)
            .pipe(
              catchError(() => {
                searchResultsGlobalStore.updateEditDataLoading(false);
                return EMPTY;
              }),
            )
            .subscribe((selectedObjects) => {
              searchResultsGlobalStore.updateEditDataLoading(false);

              if (selectedObjects.length === 0) {
                return;
              }

              if (store.isEditDataDialogRequired()) {
                patchState(store, { editDataWarningVisible: true });
              } else {
                navigateToManageData(store.editableObjectIdsForEditing());
              }
            });
        },
        navigateToManageData: (objectIds: UUID[]) => {
          navigateToManageData(objectIds);
        },
        closeEditDataWarningDialog: () => {
          patchState(store, { editDataWarningVisible: false });
        },
        onReviewAppliedFiltersClick: (): void => {
          analyticsService.sendEvent('no_results_button_clicked', {
            search_term: filterGlobalStore.searchTerm(),
            is_nls: false,
          });
          filterGlobalStore.updateAdvancedFilterVisible(true);
        },
        trackNoResultsClearFiltersClick: (): void => {
          analyticsService.sendEvent('no_results_button_clicked', {
            search_term: filterGlobalStore.smartSearchQuery(),
            is_nls: true,
          });
        },
        updateSmartFiltersHasError(status: boolean): void {
          updateSmartFiltersHasError(status);
        },
        updateSmartFiltersErrorDialogVisibility(visible: boolean): void {
          updateSmartFiltersErrorDialogVisibility(visible);
        },
        toggleSmartFiltersForceRetry(): void {
          patchState(store, {
            smartFiltersForceRetry: !store.smartFiltersForceRetry(),
          });
        },
        updateSmartFiltersRefineQuery(smartFiltersRefineQuery: boolean): void {
          updateSmartFiltersRefineQuery(smartFiltersRefineQuery);
        },
        getSmartSearchFilters: rxMethod<SmartSearchParameters>(
          pipe(
            filter(
              ({ searchTerm }) =>
                store.aiSearchToggleOn() &&
                store.context() === 'search' &&
                !!filterGlobalStore.smartSearchQuery() &&
                searchTerm !== store.loadedCypherUserQuery(),
            ),
            tap(() => {
              updateTypeAheadSearchTerm('');
              contentDetailsGlobalStore.updateIsDrawerOpen(false);
              updateSmartFiltersHasError(false);
              updateSmartFiltersErrorDialogVisibility(false);
              searchResultsGlobalStore.updateSearchResultsLoading(true);
              searchResultsGlobalStore.updateSmartFiltersLoading(true);
              updateSmartFiltersRefineQuery(false);
              resetCypherResults();
            }),
            switchMap(({ searchTerm }) =>
              store.featureFlagsStore.aiSearchCypherQueryEnabled()
                ? runCypherSmartSearch(searchTerm)
                : runFilterSmartSearch(searchTerm),
            ),
          ),
        ),
        getSearchResultItemsPage(index: number): void {
          if (
            filterGlobalStore.aiSearchResultsActive() &&
            store.cypherSearchId() !== null
          ) {
            getCypherResultsPage(index);
            searchResultsGlobalStore.updateCurrentPageIndex(index);
            navigateToSearchUrl(store.searchParams(), index + 1);
            return;
          }
          getObjectSearchResultItemsPage(index);
        },
        getObjectSearchResultItemsPage,
        updateObjectResultsSorting,
        getWorkspaceSearchResultItemsPage,
        getViewSearchResultItemsPage,
        getWorkspaceSearchResultItems: rxMethod<SearchParameters>(
          pipe(
            filter(canLoadSearchResults),
            tap(() => {
              patchState(store, {
                workspaceSearchResultsLoading: true,
                workspaceSearchResultsFailed: false,
                workspaceCurrentPageIndex: 0,
              });
            }),
            switchMap((searchParams) =>
              (isDefaultSearch(searchParams.searchTerm)
                ? lastOpenedWorkspacesPages$(searchParams.limit)
                : workspaceSearchResultsService
                    .paginatedWorkspaceSearch$(searchParams)
                    .pipe(
                      switchMap(({ results, totalCount, error }) =>
                        workspacesService
                          .mapSearchResultsToWorkspaceItems(results)
                          .pipe(
                            map((workspaceSearchResultItems) => ({
                              workspaceSearchResultItems,
                              totalCount,
                              error,
                            })),
                          ),
                      ),
                    )
              ).pipe(
                tap(({ workspaceSearchResultItems, totalCount, error }) => {
                  if (error !== undefined) {
                    notifySearchResultsFailed(
                      'searchResultsErrorToast.workspacesDetails',
                    );
                  }
                  patchState(store, {
                    workspaceSearchResultItems,
                    workspaceTotalCount: totalCount,
                    workspaceSearchResultsLoading: false,
                    workspaceSearchResultsFailed: error !== undefined,
                  });
                }),
                catchError(() => {
                  notifySearchResultsFailed(
                    'searchResultsErrorToast.workspacesDetails',
                  );
                  patchState(store, {
                    workspaceSearchResultsLoading: false,
                    workspaceSearchResultsFailed: true,
                  });
                  return EMPTY;
                }),
                takeUntil(cancelViewAndWorkspaceLoading$),
              ),
            ),
          ),
        ),
        getViewSearchResultItems: rxMethod<SearchParameters>(
          pipe(
            filter(canLoadSearchResults),
            tap(() => {
              patchState(store, {
                viewSearchResultsLoading: true,
                viewSearchResultsFailed: false,
                viewCurrentPageIndex: 0,
              });
            }),
            switchMap((searchParams) =>
              viewSearchResultsService.paginatedViewSearch$(searchParams).pipe(
                switchMap(({ results, totalCount, error }) =>
                  workspacesService
                    .attachViewItemPreviews(
                      mapViewSearchResultsToViewItems(results),
                    )
                    .pipe(
                      map((viewSearchResultItems) => ({
                        viewSearchResultItems,
                        totalCount,
                        error,
                      })),
                    ),
                ),
                tap(({ viewSearchResultItems, totalCount, error }) => {
                  if (error !== undefined) {
                    notifySearchResultsFailed(
                      'searchResultsErrorToast.viewsDetails',
                    );
                  }
                  patchState(store, {
                    viewSearchResultItems,
                    viewTotalCount: totalCount,
                    viewSearchResultsLoading: false,
                    viewSearchResultsFailed: error !== undefined,
                  });
                }),
                catchError(() => {
                  notifySearchResultsFailed(
                    'searchResultsErrorToast.viewsDetails',
                  );
                  patchState(store, {
                    viewSearchResultsLoading: false,
                    viewSearchResultsFailed: true,
                  });
                  return EMPTY;
                }),
                takeUntil(cancelViewAndWorkspaceLoading$),
              ),
            ),
          ),
        ),
        getSearchResultCounts: rxMethod<SearchParameters>(
          pipe(
            filter(canLoadSearchResults),
            // AI/NLS counts come from the cypher result; the regular count query
            // would overwrite the object total and re-populate view/workspace counts.
            filter(() => !store.aiSearchToggleOn()),
            tap(({ searchTerm }) => {
              trackSearchPerformed(searchTerm);
              updateSearchResultsCount();
            }),
          ),
        ),
        getObjectSearchResultItems: (searchParams: SearchParameters) => {
          loadObjectSearchResultItems({ searchParams, loadMode: 'prepare' });
        },
        refreshObjectSearchResultItems: (searchParams: SearchParameters) => {
          loadObjectSearchResultItems({ searchParams, loadMode: 'refresh' });
        },
        // Methods for Content open
        updateContentOpenDialogVisible: (visible: boolean) => {
          updateContentOpenDialogVisible(visible);
        },
        updateSelectedElements: (
          cancelObjectLoading$?: BehaviorSubject<boolean>,
        ) => {
          return updateSelectedElements(cancelObjectLoading$);
        },
        // Methods for Populations
        onObjectSelectionChange: (selection: ContentSelection) => {
          const itemsCount =
            selection.selectedItemsCount ?? selection.selectedItems.length;
          const unselectedItemsCount = selection.unselectedItems?.length ?? 0;
          const effectiveItemsCount =
            itemsCount > 0
              ? itemsCount
              : searchResultsGlobalStore.totalObjectsCount() -
                unselectedItemsCount;
          const effectiveFetchAllItems =
            itemsCount > 0 ? selection.fetchAllItems : true;
          populationsGlobalStore.updateSelectedObjectCount(itemsCount);
          updateContentSelection({
            selectedItems: selection.selectedItems,
            fetchAllItems: effectiveFetchAllItems,
            unselectedItems: selection.unselectedItems ?? [],
            selectedItemsCategory: selection.selectedItemsCategory,
            selectedItemsCount: effectiveItemsCount,
          });

          // Update insight suggestions based on selection
          const objectItems = selection.selectedItems.filter(
            (item) => item.scope === 'object',
          );
          const hasObjectSelection =
            objectItems.length > 0 && !selection.fetchAllItems;
          if (hasObjectSelection) {
            const ids = objectItems.map((item) => item.id);
            assertIsUUIDs(ids);
            selectedObjectIds = ids;
          } else {
            selectedObjectIds = null;
          }
          aiAssistantV2Store.invalidateContext();
        },
        updateCreatePopulationModalVisible: (
          createPopulationModalVisible: boolean,
        ) => {
          populationsGlobalStore.updateCreatePopulationModalVisible(
            createPopulationModalVisible,
          );
        },
        updateDataForPopulationLoading: (dataForPopulationLoading: boolean) => {
          populationsGlobalStore.updateDataForPopulationLoading(
            dataForPopulationLoading,
          );
        },
        updateChosenPopulationTypeAndModalVisibility: (
          chosenPopulationType: PopulationType,
        ) => {
          populationsGlobalStore.updateChosenPopulationType(
            chosenPopulationType,
          );
          populationsGlobalStore.updateCreatePopulationModalVisible(true);
        },
        fetchAttributeCategoryDefinitions: () => {
          fetchAttributeCategoryDefinitions(filterGlobalStore);
        },
        getContentOpenObjects: rxMethod<boolean>(
          pipe(
            switchMap((contentOpenDialogVisible) => {
              if (contentOpenDialogVisible) {
                // Single drawer "add to" is resolved by loadDrawerAddToObject.
                if (store.drawerAddToObjectId()) {
                  return EMPTY;
                }
                store.cancelObjectLoading$.next(false);
                return updateSelectedElements(store.cancelObjectLoading$);
              } else {
                updateContentSelection(
                  store.contentSelectionBackup() ?? store.contentSelection(),
                );
                updateContentSelectionBackup(undefined);
                return EMPTY;
              }
            }),
          ),
        ),
        loadDrawerAddToObject: rxMethod<string | undefined>(
          pipe(
            switchMap((objectId) => {
              if (!objectId || !isUUID(objectId)) {
                patchState(store, { drawerAddToObjects: [] });
                return EMPTY;
              }
              searchResultsGlobalStore.updateAllRemainingSearchResultsLoading(
                true,
              );
              return from(fetchElementCollectionForIds([objectId])).pipe(
                tap((collection) => {
                  patchState(store, {
                    drawerAddToObjects: collection ? collection.objects : [],
                  });
                }),
                catchError(() => {
                  patchState(store, { drawerAddToObjects: [] });
                  return EMPTY;
                }),
                finalize(() =>
                  searchResultsGlobalStore.updateAllRemainingSearchResultsLoading(
                    false,
                  ),
                ),
              );
            }),
          ),
        ),
        // Methods for Users
        searchUsers: rxMethod<string>(
          pipe(
            debounceTime(200),
            distinctUntilChanged(),
            tap(() => {
              patchState(store, {
                usersLoading: true,
                users: [],
              });
            }),
            switchMap((searchTerm) => {
              if (searchTerm?.trim()) {
                return usersService
                  .searchUsers({ searchTerm, limit: 8 })
                  .results.pipe(
                    tap((users) => {
                      patchState(store, {
                        users,
                        usersLoading: false,
                      });
                    }),
                  );
              }
              patchState(store, {
                users: [],
                usersLoading: false,
              });
              return of([]);
            }),
            finalize(() => {
              patchState(store, {
                usersLoading: false,
              });
            }),
          ),
        ),
        updateUsersSearchTerm: (usersSearchTerm: string) => {
          patchState(store, {
            usersSearchTerm,
          });
        },
        updateSearchResultItemName: rxMethod<ContentItemDetails | undefined>(
          pipe(
            filter(
              (details): details is ContentItemDetails => details !== undefined,
            ),
            tap((details) => {
              const searchResultItems = store.searchResultItems();
              const matchingItem = searchResultItems.find(
                (item) => item.id === details.id,
              );
              if (!matchingItem || matchingItem.name === details.name) {
                return;
              }

              patchState(store, {
                searchResultItems: searchResultItems.map((item) =>
                  item.id === details.id
                    ? { ...item, name: details.name }
                    : item,
                ),
              });
            }),
          ),
        ),
        // Methods for search url parameters
        updateScopeFiltersFromUrlParam: rxMethod<string | undefined>(
          pipe(
            distinctUntilChanged(),
            switchMap((scopeFiltersUrlParam) => {
              if (scopeFiltersUrlParam === undefined) return EMPTY;

              let newFilters: SearchScope[];
              try {
                newFilters = urlParamToScopeFilters(scopeFiltersUrlParam);
              } catch (error) {
                notifyInvalidUrlParam(
                  'invalidSearchQueryToast.scopeFiltersDetails',
                  error,
                );

                return EMPTY;
              }

              const currentFilters =
                filterGlobalStore.advancedFilter().scopeFilters;

              const shouldUpdate = !areScopeFiltersEqual(
                currentFilters,
                newFilters,
              );
              if (!shouldUpdate) return EMPTY;

              filterGlobalStore.updateAdvancedFilter({
                ...filterGlobalStore.advancedFilter(),
                scopeFilters: newFilters,
              });

              return EMPTY;
            }),
          ),
        ),
        updateTypeFiltersFromUrlParam: rxMethod<string | undefined>(
          pipe(
            distinctUntilChanged(),
            switchMap((typeFiltersUrlParam) => {
              if (typeFiltersUrlParam === undefined) return EMPTY;

              let newFilters;
              try {
                newFilters = urlParamToElementTypeFilters(typeFiltersUrlParam);
              } catch (error) {
                notifyInvalidUrlParam(
                  'invalidSearchQueryToast.typeFiltersDetails',
                  error,
                );

                return EMPTY;
              }

              const currentFilters =
                filterGlobalStore.advancedFilter().elementTypeFilters;

              const shouldUpdate = !areTypeFiltersEqual(
                currentFilters,
                newFilters,
              );
              if (!shouldUpdate) return EMPTY;

              filterGlobalStore.updateAdvancedFilter({
                ...filterGlobalStore.advancedFilter(),
                elementTypeFilters: newFilters,
              });

              return EMPTY;
            }),
          ),
        ),
        updateAttributeFiltersFromUrlContext:
          rxMethod<AttributeFiltersUrlContext>(
            pipe(
              distinctUntilChanged(
                (a, b) =>
                  a.attributeFiltersUrlParam === b.attributeFiltersUrlParam &&
                  a.attributeCategoryDefinitions.length ===
                    b.attributeCategoryDefinitions.length,
              ),
              switchMap(
                ({
                  attributeFiltersUrlParam,
                  attributeCategoryDefinitions,
                }) => {
                  if (attributeFiltersUrlParam === undefined) return EMPTY;
                  if (!attributeCategoryDefinitions?.length) return EMPTY;

                  let attributeFiltersAndObjectIds: AttributeFilterValueAndObjectId[];
                  try {
                    attributeFiltersAndObjectIds =
                      urlParamToAttributeFiltersAndObjectIds(
                        attributeFiltersUrlParam,
                        attributeCategoryDefinitions,
                      );
                  } catch (error) {
                    notifyInvalidUrlParam(
                      'invalidSearchQueryToast.attributeFiltersDetails',
                      error,
                    );
                    return EMPTY;
                  }

                  const objectIds = Array.from(
                    new Set(
                      attributeFiltersAndObjectIds
                        .map((param) => param.objectId)
                        .filter((id): id is UUID => id !== undefined),
                    ),
                  );

                  const baseAttributeFilters = attributeFiltersAndObjectIds
                    .map((param) => param.filterValue)
                    .filter(
                      (filter): filter is AttributeFilterValue =>
                        filter !== undefined,
                    );

                  const objectNames$ = objectIds.length
                    ? searchService.getObjectNamesByIds(objectIds).pipe(take(1))
                    : of(undefined as undefined | Map<UUID, string>);

                  return objectNames$.pipe(
                    map((objectNames) => {
                      try {
                        const newFilters = objectNames
                          ? updateAttributeFiltersObjectsNames(
                              baseAttributeFilters,
                              objectNames,
                            )
                          : baseAttributeFilters;
                        return newFilters;
                      } catch (error) {
                        notifyInvalidUrlParam(
                          'invalidSearchQueryToast.attributeFiltersDetails',
                          error,
                        );
                        return baseAttributeFilters;
                      }
                    }),
                    tap((newFilters) => {
                      const currentFilters =
                        filterGlobalStore.advancedFilter().attributeFilters;

                      const shouldUpdate = !areAttributeFiltersEqual(
                        currentFilters,
                        newFilters,
                      );

                      if (!shouldUpdate) return;
                      filterGlobalStore.updateAdvancedFilter({
                        ...filterGlobalStore.advancedFilter(),
                        attributeFilters: newFilters,
                      });
                    }),
                  );
                },
              ),
            ),
          ),
        updateRelationFiltersFromUrlParam: rxMethod<string | undefined>(
          pipe(
            distinctUntilChanged(),
            switchMap((relationFiltersUrlParam) => {
              if (relationFiltersUrlParam === undefined) return EMPTY;
              let relationFiltersParameters: RelationFilterValueAndObjectId[];

              try {
                relationFiltersParameters =
                  urlParamToRelationFiltersAndObjectIds(
                    relationFiltersUrlParam,
                  );
              } catch (error) {
                notifyInvalidUrlParam(
                  'invalidSearchQueryToast.relationFiltersDetails',
                  error,
                );

                return EMPTY;
              }

              const referencedObjectIds = Array.from(
                new Set(
                  relationFiltersParameters
                    .map((param) => param.objectId)
                    .filter((id): id is UUID => id !== undefined),
                ),
              );

              const baseRelationFilters = relationFiltersParameters
                .map((param) => param.filterValue)
                .filter(
                  (filter): filter is RelationFilterValue =>
                    filter !== undefined,
                );

              const objectNames$ = referencedObjectIds.length
                ? searchService
                    .getObjectNamesByIds(referencedObjectIds)
                    .pipe(take(1))
                : of(undefined as undefined | Map<UUID, string>);

              return objectNames$.pipe(
                map((objectNames) => {
                  try {
                    const newFilters = objectNames
                      ? updateRelationFiltersObjectsNames(
                          baseRelationFilters,
                          objectNames,
                        )
                      : baseRelationFilters;
                    return newFilters;
                  } catch (error) {
                    notifyInvalidUrlParam(
                      'invalidSearchQueryToast.relationFiltersDetails',
                      error,
                    );
                    return baseRelationFilters;
                  }
                }),
                tap((newFilters) => {
                  const currentFilters =
                    filterGlobalStore.advancedFilter().relationFilters;

                  const shouldUpdate = !areRelationFiltersEqual(
                    currentFilters,
                    newFilters,
                  );

                  if (!shouldUpdate) return;
                  filterGlobalStore.updateAdvancedFilter({
                    ...filterGlobalStore.advancedFilter(),
                    relationFilters: newFilters,
                  });
                }),
              );
            }),
          ),
        ),
        updateSystemFiltersFromUrlParam: rxMethod<string | undefined>(
          pipe(
            distinctUntilChanged(),
            switchMap((systemFiltersUrlParam) => {
              if (systemFiltersUrlParam === undefined) return EMPTY;
              let systemAttributeFilter;
              try {
                systemAttributeFilter = urlParamToSystemAttributesFilter(
                  systemFiltersUrlParam,
                );
              } catch (error) {
                notifyInvalidUrlParam(
                  'invalidSearchQueryToast.systemAttributeFiltersDetails',
                  error,
                );

                return EMPTY;
              }
              const userDetails$: Observable<
                SystemAttributesUser[] | undefined
              > = systemAttributeFilter?.createdBy?.userId
                ? usersService
                    .searchUsers({
                      ids: [systemAttributeFilter.createdBy.userId],
                      limit: 1,
                    })
                    .results.pipe(take(1))
                : of(undefined);
              return userDetails$.pipe(
                map((userDetails) => {
                  try {
                    return updateSystemAttributeFilterUserDetails(
                      systemAttributeFilter,
                      userDetails,
                    );
                  } catch (error) {
                    notifyInvalidUrlParam(
                      'invalidSearchQueryToast.systemAttributeFiltersDetails',
                      error,
                    );
                    return systemAttributeFilter;
                  }
                }),
                tap((newFilter) => {
                  const currentFilter =
                    filterGlobalStore.advancedFilter().systemAttributesFilter;
                  const shouldUpdate = !areSystemFiltersEqual(
                    currentFilter,
                    newFilter,
                  );
                  if (!shouldUpdate) return;
                  filterGlobalStore.updateAdvancedFilter({
                    ...filterGlobalStore.advancedFilter(),
                    systemAttributesFilter: newFilter,
                  });
                }),
              );
            }),
          ),
        ),
        updateSearchTermFromUrlParam: rxMethod<string | undefined>(
          pipe(
            distinctUntilChanged(),
            switchMap((searchTermParam) => {
              if (searchTermParam === undefined) return EMPTY;

              let newTerms: string;
              try {
                newTerms = urlParamToSearchTerm(searchTermParam);
              } catch (error) {
                notifyInvalidUrlParam(
                  'invalidSearchQueryToast.searchTermDetails',
                  error,
                );

                return EMPTY;
              }

              const currentTerms = filterGlobalStore.searchTerm();
              const shouldUpdate = !areSearchTermsEqual(currentTerms, newTerms);
              if (!shouldUpdate) return EMPTY;

              filterGlobalStore.updateSearchTerm(newTerms);
              return EMPTY;
            }),
          ),
        ),
        hydrateSearchFromDrawerObjectIdUrlParam: rxMethod<string | undefined>(
          pipe(
            distinctUntilChanged(),
            switchMap((rawObjectId) => {
              if (rawObjectId === undefined) {
                return EMPTY;
              }

              let objectId: UUID;
              try {
                objectId = urlParamToObjectId(rawObjectId);
              } catch (error) {
                notifyInvalidUrlParam(
                  'invalidSearchQueryToast.objectIdDetails',
                  error,
                );
                return EMPTY;
              }

              return from(
                knowledgeGraphClient
                  .getObjects({ filter: { ids: [objectId] } })
                  .getAll(),
              ).pipe(
                tap((objects) => {
                  const object = objects[0];
                  if (!object) {
                    // The url names this object, so a drawer left open on
                    // another one would contradict it.
                    contentDetailsGlobalStore.updateIsDrawerOpen(false);
                    notifySharedObjectUnavailable();
                    return;
                  }

                  // Open the drawer from the object itself: waiting for it to
                  // turn up in the search results tied it to their timing.
                  contentDetailsGlobalStore.updateContentItem(
                    objectDtoToContentItem(object),
                  );
                  contentDetailsGlobalStore.updateIsDrawerOpen(true);
                  keepDrawerOpenIntent = { confirmObjectId: objectId };

                  updateAiSearchToggleOn(false);
                  filterGlobalStore.updateAiSearchResultsActive(false);
                  resetCypherSearch();
                  filterGlobalStore.updateSearchTermAndAdvancedFilter(
                    object.name,
                    {
                      ...defaultAdvancedFilter,
                      scopeFilters: ['OBJECT'],
                      elementTypeFilters: [object.type],
                    },
                  );
                }),
                catchError((error) => {
                  console.error('Shared object lookup failed:', error);
                  notifySearchResultsFailed(
                    'searchResultsErrorToast.sharedObjectDetails',
                  );
                  return EMPTY;
                }),
              );
            }),
          ),
        ),
        updateCypherSearchIdFromUrlParam: rxMethod<string | undefined>(
          pipe(
            distinctUntilChanged(),
            switchMap((rawSearchId) => {
              if (rawSearchId === undefined) return EMPTY;

              let searchId: UUID;
              try {
                searchId = urlParamToCypherSearchId(rawSearchId);
              } catch (error) {
                notifyInvalidUrlParam(
                  'invalidSearchQueryToast.cypherSearchIdDetails',
                  error,
                );
                return EMPTY;
              }

              if (store.cypherSearchId() === searchId) return EMPTY;

              const pageIndexParam = store.pageIndexUrlParam();
              let pageIndex = 0;
              if (pageIndexParam !== undefined) {
                try {
                  pageIndex = urlParamToSearchPageIndex(pageIndexParam) - 1;
                } catch {
                  pageIndex = 0;
                }
              }

              updateAiSearchToggleOn(true);
              filterGlobalStore.updateAiSearchResultsActive(true);
              patchState(store, { cypherSearchId: searchId });
              searchResultsGlobalStore.updateCurrentPageIndex(pageIndex);
              getCypherResultsPage(pageIndex);
              return EMPTY;
            }),
          ),
        ),
        updateSearchPageIndexFromUrlParam: rxMethod<string | undefined>(
          pipe(
            distinctUntilChanged(),
            switchMap((pageIndexParam) => {
              if (pageIndexParam === undefined) {
                if (searchResultsGlobalStore.currentPageIndex() !== 0) {
                  searchResultsGlobalStore.updateCurrentPageIndex(0);
                  loadSearchResultsPageFromUrl(0);
                }

                return EMPTY;
              }

              let newPageIndex: number;
              try {
                newPageIndex = urlParamToSearchPageIndex(pageIndexParam) - 1;
              } catch (error) {
                notifyInvalidUrlParam(
                  'invalidSearchQueryToast.pageIndexDetails',
                  error,
                );

                return EMPTY;
              }

              const currentPageIndex =
                searchResultsGlobalStore.currentPageIndex();
              const pageIndexesDiffer = !areSearchPageIndexesEqual(
                currentPageIndex,
                newPageIndex,
              );
              if (!pageIndexesDiffer) return EMPTY;

              searchResultsGlobalStore.updateCurrentPageIndex(newPageIndex);
              loadSearchResultsPageFromUrl(newPageIndex);
              return EMPTY;
            }),
          ),
        ),
        updateSearchUrlParamsFromQueryParams: (parameters: Params) => {
          const drawerObjectIdUrlParam = parameters['drawerObjectId'];
          if (drawerObjectIdUrlParam !== undefined) {
            applyDrawerObjectIdUrlParams(drawerObjectIdUrlParam);
            return;
          }

          applySearchQueryUrlParams(parameters);
        },
        applySearchFromAssistant: rxMethod<SearchData>(
          pipe(
            tap((data) => {
              filterGlobalStore.updateNlsTraceParent(data.traceParent);
              searchResultsGlobalStore.updateCurrentPageIndex(0);

              switch (data.kind) {
                case 'cypher':
                  applyCypherSearchFromAssistant(data);
                  break;
                case 'semantic':
                  applySemanticSearchFromAssistant(data);
                  break;
                default:
                  applyFilterSearchFromAssistant(data);
              }
            }),
          ),
        ),
        addObjectsToWorkspaceFromAssistant: rxMethod<AddToWorkspaceData>(
          pipe(
            switchMap(({ workspaceId, workspaceName }) => {
              if (!isUUID(workspaceId)) {
                return notifyAddToWorkspaceFromAssistantFailed(
                  new Error(
                    `Invalid workspaceId received from assistant: ${workspaceId}`,
                  ),
                );
              }
              // Clear any lingering cancellation from a prior flow, then resolve
              // the selection the same way the content-open dialog does so
              // "Select all" (fetchAllItems) is respected.
              store.cancelObjectLoading$.next(false);
              return updateSelectedElements(store.cancelObjectLoading$).pipe(
                switchMap((objects) => {
                  if (objects.length === 0) return EMPTY;
                  const workspace: WorkspaceBase = {
                    id: workspaceId,
                    name: workspaceName,
                    archived: false,
                  };
                  return contentOpenService.openElementCollectionInWorkspace(
                    { objects, relations: [] },
                    workspace,
                  );
                }),
                catchError(notifyAddToWorkspaceFromAssistantFailed),
              );
            }),
          ),
        ),
      };
    },
  ),
  withHooks((store) => {
    const filterGlobalStore = inject(FilterGlobalStore);
    const searchResultsGlobalStore = inject(SearchResultsGlobalStore);
    const aiAssistantV2Store = inject(AiAssistantV2Store);
    const contentDetailsStore = inject(ContentDetailsGlobalStore);
    const searchSessionService = inject(SearchSessionService);

    /**
     * Resets the results table whenever the object type the filters resolve
     * changes, exactly as the Reset table menu item does.
     *
     * @remarks
     * A different type is a different search, so the attribute columns chosen
     * for the previous one no longer apply. Resetting also covers the case the
     * stored layout survives validation on its own: two types sharing an
     * attribute category would keep a column that now means something else.
     *
     * Whatever the effect sees first becomes the baseline, rather than the type
     * read when the store starts. The url parameters hydrate the filters during
     * `onInit`, so a baseline taken there would already be stale by the first
     * run and would wipe the layout of anyone returning to a typed search url.
     * `undefined` is itself a resolved value, hence the sentinel.
     */
    function resetTableOnResolvedObjectTypeChange(): void {
      let lastResolvedObjectType: ObjectType | undefined | 'unset' = 'unset';
      effect(() => {
        const resolvedObjectType = filterGlobalStore.resolvedObjectType();
        const isFirstRun = lastResolvedObjectType === 'unset';
        lastResolvedObjectType = resolvedObjectType;
        if (isFirstRun) {
          return;
        }
        // Untracked, or the effect would follow whatever the reset reads too:
        // clearing the sort reads `searchParams`, which every filter change and
        // every validated search term invalidates. The resolved type is meant
        // to be the only trigger.
        untracked(() => {
          searchResultsGlobalStore.resetObjectResultsTableLayout();
          // Clearing a sort navigates, so only do it when one is actually set:
          // `null` is what an already-cleared sort reads as, `undefined` the
          // value it starts at.
          if (isNotUndefinedOrNull(store.objectResultsSorting())) {
            store.updateObjectResultsSorting(undefined);
          }
        });
      });
    }

    return {
      onInit() {
        resetTableOnResolvedObjectTypeChange();

        store.updateAiSearchToggleOn(
          searchSessionService.getAiSearchToggleOnFromSession(),
        );

        store.fetchAuthenticatedUser();
        store.fetchAttributeCategoryDefinitions();

        filterGlobalStore.updateDefaultScopeFilterOptions(
          getScopeFilterOptions(),
        );

        store.getSmartSearchFilters(store.smartSearchParams);
        // Only counts are fetched up front (for the tab badges); each tab's
        // results are loaded on demand by the container when it is activated.
        store.getSearchResultCounts(store.searchParams);
        store.getResultSuggestions(store.typeAheadSearchTerm);
        store.getCombinedSuggestions(store.suggestionParams);
        store.searchUsers(store.usersSearchTerm);
        store.getSmartSearchSuggestions(store.smartSuggestionParams);
        store.getContentOpenObjects(store.contentOpenDialogVisible);
        store.loadDrawerAddToObject(store.drawerAddToObjectId);

        store.updateSearchResultItemName(
          contentDetailsStore.contentItemDetails,
        );

        store.updateScopeFiltersFromUrlParam(store.scopeFiltersUrlParam);

        store.updateTypeFiltersFromUrlParam(store.typeFiltersUrlParam);

        store.updateAttributeFiltersFromUrlContext(
          store.attributeFiltersUrlContext,
        );

        store.updateRelationFiltersFromUrlParam(store.relationFiltersUrlParam);

        store.updateSystemFiltersFromUrlParam(store.systemFiltersUrlParam);

        store.updateSearchTermFromUrlParam(store.searchTermUrlParam);

        store.updateSearchPageIndexFromUrlParam(store.pageIndexUrlParam);

        store.hydrateSearchFromDrawerObjectIdUrlParam(
          store.drawerObjectIdUrlParam,
        );

        store.updateCypherSearchIdFromUrlParam(store.cypherSearchIdUrlParam);

        const searchSdk: SearchApi = {
          applyFilter: (data) => store.applySearchFromAssistant(data),
          addObjectsToWorkspace: (data) =>
            store.addObjectsToWorkspaceFromAssistant(data),
        };
        aiAssistantV2Store.setSearch(searchSdk);
      },
      onDestroy() {
        aiAssistantV2Store.setSearch(null);
        store._cancelSmartFiltersLoadingSubject.next(true);
        store.cancelObjectLoading$.next(true);
        store._cancelSmartFiltersLoadingSubject.complete();
        store.cancelObjectLoading$.complete();
      },
    };
  }),
);

export type SearchContainerComponentStore = InstanceType<
  typeof SearchContainerComponentStore
>;
